import type { FocusEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import Link from 'next/link';
import { useEffect, useRef } from 'react';

import { ContactAvatar, OrgAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button, Input } from '@poseidon/components/Input2';
import { Kbd } from '@poseidon/components/Kbd';
import { Arrows, Communication, Files, General, Maps, Users } from '@poseidon/components/StrokeIcons';

import type {
  GlobalSearchResults,
  SearchFacet,
  SearchGroupKey,
  SearchItem,
} from '~/components/LeftNav/LeftNavSearch/leftNavSearch.model';
import {
  GROUP_SEE_ALL_LABEL,
  SEARCH_GROUPS,
  SEE_ALL_ID,
  buildSearchView,
  defaultItem,
  groupAllId,
  groupSeeAllHref,
  isSearchable,
  moveActive,
  searchItemHref,
} from '~/components/LeftNav/LeftNavSearch/leftNavSearch.model';

export type LeftNavSearchPureProps = {
  /** Collapsed nav: an icon button that opens the popover, which then carries its own search box. */
  minimized: boolean;
  isMac: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  /** Whether the popover is open. It opens on focus, with quick actions until there is a query. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Results for EXACTLY the text in the box; undefined while that answer is pending (never a stale one). */
  results: GlobalSearchResults | undefined;
  /** A search is in flight and there is nothing to show for it yet. */
  isLoading: boolean;
  facet: SearchFacet;
  onFacetChange: (facet: SearchFacet) => void;
  activeId: string | null;
  onActiveIdChange: (id: string | null) => void;
  /** Keyboard Enter / row click. The row links still carry real hrefs (middle-click, open in new tab). */
  onSelect: (item: SearchItem) => void;
  /** The search box, so the container can focus it on ⌘K. */
  inputRef: RefObject<HTMLInputElement | null>;
};

// Stable middleware identity: `flip`/`shift` keep the popover on-screen near the window edges.
const MIDDLEWARE = [offset(8), flip({ padding: 8 }), shift({ padding: 8 })];

/**
 * The left-nav search: a search box at the top of the nav and a portaled popover of grouped results
 * (people, firms, campaigns, snippets, content), with ↑/↓/Enter, a facet chip row, per-group "All …" links, quick actions and a final "Search all people"
 * that hands the query to the full search at /prospects. Data-free — `LeftNavSearch` owns the query,
 * debounce, tRPC call, ⌘K and navigation.
 */
export function LeftNavSearchPure(props: LeftNavSearchPureProps) {
  const { minimized, query, open, onOpenChange, facet, activeId, onActiveIdChange, onSelect, inputRef } = props;
  const trimmed = query.trim();
  // "::" is two characters but nothing to search: gate on what the search will actually see.
  const longEnough = isSearchable(query);
  const { sections, items, counts } = buildSearchView(props.results, facet, query);
  const hasResults = sections.some((s) => s.key !== 'actions');
  const listRef = useRef<HTMLDivElement>(null);
  // The combobox only points at the listbox while the listbox exists.
  const listboxId = open && items.length > 0 ? LISTBOX_ID : undefined;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement: minimized ? 'right-start' : 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: MIDDLEWARE,
  });
  // Outside clicks close it; Escape is handled by the search box itself (the shared esc stack skips inputs).
  const dismiss = useDismiss(context, { escapeKey: false });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  // Keep the active row in view while arrowing.
  useEffect(() => {
    if (activeId == null) return;
    listRef.current?.querySelector(`[data-search-id="${CSS.escape(activeId)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const close = () => {
    onOpenChange(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) onOpenChange(true);
      onActiveIdChange(moveActive(items, activeId, e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Enter') {
      // Before a real query, Enter only acts on a row the user arrowed to (no surprise default).
      const item = items.find((it) => it.id === activeId) ?? (longEnough ? defaultItem(items) : undefined);
      if (item == null) return;
      e.preventDefault();
      onSelect(item);
    }
  };

  // Tabbing out of the search (not into the popover) closes it, like clicking elsewhere does.
  const onBlur = (e: FocusEvent) => {
    const next = e.relatedTarget;
    if (next instanceof Node && (refs.floating.current?.contains(next) || refs.domReference.current?.contains(next))) return;
    onOpenChange(false);
  };

  const pickFacet = (next: SearchFacet) => {
    props.onFacetChange(next);
    inputRef.current?.focus();
  };

  const searchInput = (
    <Input
      ref={inputRef}
      className="w-full"
      size="md"
      icon={<General.SearchSm />}
      rIcon={minimized ? undefined : <ShortcutHint isMac={props.isMac} />}
      placeholder="Search"
      value={query}
      onChange={(e) => {
        props.onQueryChange(e.target.value);
        onOpenChange(true);
      }}
      onFocus={() => onOpenChange(true)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      autoFocus={minimized}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={listboxId != null}
      aria-controls={listboxId}
      aria-activedescendant={listboxId != null && activeId != null ? optionId(activeId) : undefined}
      aria-label="Search people, firms, campaigns, snippets and content"
      data-testid="left-nav-search-input"
    />
  );

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()} data-testid="left-nav-search">
        {minimized && (
          <Button
            text
            size="lg"
            className="w-full justify-center"
            icon={<General.SearchSm />}
            tooltip={`Search (${props.isMac ? '⌘' : 'Ctrl+'}K)`}
            aria-label="Search"
            onClick={() => !open && onOpenChange(true)}
            data-testid="left-nav-search-button"
          />
        )}
        {!minimized && searchInput}
      </div>

      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            onBlur={onBlur}
            data-testid="left-nav-search-popover"
            className="z-50 flex max-h-[min(720px,85vh)] w-[440px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left font-sans text-sm text-gray-700 shadow-lg dark:border-slate-700 dark:bg-slate-900 dark:text-gray-200">
            {minimized && <div className="border-b border-gray-200 p-2 dark:border-slate-700">{searchInput}</div>}

            {!longEnough && (
              <p className="px-4 pt-3 pb-1 text-gray-500 dark:text-gray-400" data-testid="left-nav-search-hint">
                Type to search people, firms, campaigns, snippets and content.
              </p>
            )}

            {longEnough && <FacetRow facet={facet} counts={counts} onPick={pickFacet} />}

            {longEnough && props.isLoading && !hasResults && (
              <p className="px-4 py-5 text-center text-gray-500" data-testid="left-nav-search-loading">
                Searching…
              </p>
            )}
            {longEnough && !props.isLoading && !hasResults && (
              <p className="px-4 py-5 text-center text-gray-500" data-testid="left-nav-search-empty">
                No quick matches for “{trimmed}”.
              </p>
            )}

            {items.length > 0 && (
              // One listbox holds every option, the pinned "Search all people" row included, so
              // aria-activedescendant always names an option inside the element aria-controls points at.
              <div
                id={LISTBOX_ID}
                role="listbox"
                aria-label="Search results"
                className="flex min-h-0 grow flex-col"
                data-testid="left-nav-search-list">
                <div ref={listRef} className="min-h-0 grow overflow-y-auto py-1">
                  {sections.map((section) => (
                    <div
                      key={section.key}
                      role="group"
                      aria-labelledby={`left-nav-search-heading-${section.key}`}
                      data-testid={`left-nav-search-group-${section.key}`}>
                      <div className="flex items-center justify-between px-4 pt-2 pb-1">
                        <span
                          id={`left-nav-search-heading-${section.key}`}
                          className="text-[11px] font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                          {section.label}
                        </span>
                        {section.key !== 'actions' && (
                          <GroupSeeAllOption
                            group={section.key}
                            query={trimmed}
                            active={activeId === groupAllId(section.key)}
                            onHover={() => onActiveIdChange(groupAllId(section.key))}
                            onPick={() => onSelect({ kind: 'groupAll', id: groupAllId(section.key), group: section.key })}
                          />
                        )}
                      </div>
                      {section.items.map((item) => (
                        <ResultRow
                          key={item.id}
                          item={item}
                          query={trimmed}
                          active={item.id === activeId}
                          onHover={() => onActiveIdChange(item.id)}
                          onPick={() => onSelect(item)}
                        />
                      ))}
                    </div>
                  ))}
                </div>

                {longEnough && (
                  <div role="group" aria-label="Full search" className="border-t border-gray-200 py-1 dark:border-slate-700">
                    <ResultRow
                      item={{ kind: 'seeAll', id: SEE_ALL_ID }}
                      query={trimmed}
                      active={activeId === SEE_ALL_ID}
                      onHover={() => onActiveIdChange(SEE_ALL_ID)}
                      onPick={() => onSelect({ kind: 'seeAll', id: SEE_ALL_ID })}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-gray-200 px-4 py-1.5 text-xs text-gray-500 dark:border-slate-700 dark:text-gray-400">
              <kbd className="font-sans">↑↓</kbd> move · <kbd className="font-sans">↵</kbd> open ·{' '}
              <kbd className="font-sans">esc</kbd> close
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

const LISTBOX_ID = 'left-nav-search-list';
const optionId = (itemId: string) => `left-nav-search-${itemId}`;

/**
 * A group's "All …" link: the full list behind the group, already filtered by the query. It is an option
 * in the listbox like any row (↑/↓ reach it, Enter opens it), shown compactly in the group header.
 */
function GroupSeeAllOption({
  group,
  query,
  active,
  onHover,
  onPick,
}: {
  group: SearchGroupKey;
  query: string;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const id = groupAllId(group);
  return (
    <Link
      href={groupSeeAllHref(group, query)}
      id={optionId(id)}
      role="option"
      aria-selected={active}
      data-search-id={id}
      tabIndex={-1}
      onMouseMove={onHover}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onPick();
      }}
      data-testid={`left-nav-search-group-see-all-${group}`}
      className={cn(
        'cursor-pointer rounded px-1 text-xs text-gray-500 no-underline hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-gray-100',
        active && 'bg-blue-50 text-gray-800 hover:bg-blue-50 dark:bg-blue-900/30 dark:text-gray-100 dark:hover:bg-blue-900/30',
      )}>
      {GROUP_SEE_ALL_LABEL[group]} →
    </Link>
  );
}

function ShortcutHint({ isMac }: { isMac: boolean }) {
  return <Kbd className="whitespace-nowrap">{isMac ? '⌘K' : 'Ctrl+K'}</Kbd>;
}

function FacetRow({
  facet,
  counts,
  onPick,
}: {
  facet: SearchFacet;
  counts: Record<(typeof SEARCH_GROUPS)[number]['key'], number>;
  onPick: (facet: SearchFacet) => void;
}) {
  const groupsWithResults = SEARCH_GROUPS.filter((g) => counts[g.key] > 0);
  if (groupsWithResults.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 px-3 py-2 dark:border-slate-700" data-testid="left-nav-search-facets">
      <FacetChip selected={facet === 'all'} onClick={() => onPick('all')} testId="all">
        All
      </FacetChip>
      {groupsWithResults.map((g) => (
        <FacetChip key={g.key} selected={facet === g.key} onClick={() => onPick(g.key)} testId={g.key}>
          {g.label}
          <span className="ml-1 opacity-70">{counts[g.key]}</span>
        </FacetChip>
      ))}
    </div>
  );
}

function FacetChip({
  selected,
  onClick,
  testId,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Button
      size="2xs"
      color={selected ? 'blue' : 'gray'}
      tone={selected ? 'filled' : 'outline'}
      aria-pressed={selected}
      // Keep focus in the search box so ↑/↓ keep working after picking a facet.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-testid={`left-nav-search-facet-${testId}`}>
      {children}
    </Button>
  );
}

function ResultRow({
  item,
  query,
  active,
  onHover,
  onPick,
}: {
  item: SearchItem;
  query: string;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  return (
    <Link
      href={searchItemHref(item, query)}
      id={optionId(item.id)}
      role="option"
      aria-selected={active}
      data-search-id={item.id}
      data-testid={`left-nav-search-item-${item.id}`}
      onMouseMove={onHover}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        // Plain clicks go through the container (closes the popover, clears the box); modified clicks
        // (⌘/Ctrl/middle) keep the browser's open-in-new-tab behavior.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onPick();
      }}
      className={cn(
        'mx-1 flex cursor-pointer items-center gap-3 rounded-md px-3 py-1.5 text-gray-700 no-underline hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-800',
        active && 'bg-blue-50 hover:bg-blue-50 dark:bg-blue-900/30 dark:hover:bg-blue-900/30',
      )}>
      <RowBody item={item} query={query} />
      {active && <Arrows.ReverseLeft className="ml-auto size-3.5 shrink-0 text-gray-400" />}
    </Link>
  );
}

const ICON_CLASS = 'size-7 shrink-0 rounded-md bg-gray-100 p-1.5 text-gray-500 dark:bg-slate-800 dark:text-gray-400';

function RowText({ title, subtitle }: { title: string; subtitle?: string | null }) {
  return (
    <div className="min-w-0 grow">
      <div className="truncate font-medium">{title}</div>
      {subtitle != null && subtitle !== '' && <div className="truncate text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
    </div>
  );
}

function RowBody({ item, query }: { item: SearchItem; query: string }) {
  switch (item.kind) {
    case 'people': {
      const p = item.data;
      const name = [p.firstName, p.lastName].filter((v) => v != null && v !== '').join(' ') || 'Unnamed contact';
      const subtitle = [p.title, p.company].filter((v) => v != null && v !== '').join(' · ');
      return (
        <>
          <ContactAvatar firstName={p.firstName} lastName={p.lastName} imageSrc={p.pictureUrl} className="size-7 shrink-0" />
          <RowText title={name} subtitle={subtitle} />
        </>
      );
    }
    case 'firms':
      return (
        <>
          <OrgAvatar name={item.data.name} src={item.data.pictureUrl} className="size-7 shrink-0" />
          <RowText title={item.data.name ?? 'Unnamed firm'} />
        </>
      );
    case 'campaigns':
      return (
        <>
          <Maps.Rocket className={ICON_CLASS} />
          <RowText title={item.data.name} subtitle={item.data.ownerName} />
        </>
      );
    case 'snippets':
      return (
        <>
          <Communication.MessageTextSquare02 className={ICON_CLASS} />
          <RowText title={item.data.name || 'Untitled snippet'} subtitle={item.data.preview} />
        </>
      );
    case 'content':
      return (
        <>
          <Files.FileAttachment02 className={ICON_CLASS} />
          <RowText title={item.data.title} subtitle={contentTypeLabel(item.data.fileContentType)} />
        </>
      );
    case 'groupAll':
      return <RowText title={GROUP_SEE_ALL_LABEL[item.group]} />;
    case 'action':
      return (
        <>
          <General.UploadCloud02 className={ICON_CLASS} />
          <RowText title={item.data.label} subtitle={item.data.description} />
        </>
      );
    case 'seeAll':
      return (
        <>
          <Users.Users01 className={ICON_CLASS} />
          <div className="min-w-0 grow truncate">
            Search all people for “<span className="font-medium">{query}</span>”
          </div>
          <Arrows.ArrowRight className="size-4 shrink-0 text-gray-400" />
        </>
      );
  }
}

/** "application/pdf" → "PDF", "custom-link" → "Link", "video/mp4" → "Video". */
function contentTypeLabel(type: string): string {
  if (type === 'custom-link') return 'Link';
  if (type === 'application/pdf') return 'PDF';
  const major = type.split('/')[0] ?? '';
  if (major === 'image' || major === 'video' || major === 'audio') return major.charAt(0).toUpperCase() + major.slice(1);
  return 'File';
}
